<script setup lang="ts">
import type { RentalDateModel } from '@/types/models/RentalModel'
import {
    ActionButton,
    BaseDialog,
    DateTimePickerField,
    FullScreenLoader,
    StackLayout
} from '@rfc/rfc-components-library'
import {
    EProviders,
    type DateProviderType,
    type DateTimePickerFieldProps
} from '@rfc/rfc-components-library/types'
import moment from 'moment'
import { inject } from 'vue'

const { formatDate } = inject<DateProviderType>(EProviders.DATE, {})

type Props = {
    loading: boolean
    label?: string
    title?: string
    valueDate?: RentalDateModel
    startDateProps?: DateTimePickerFieldProps & Record<string, unknown>
    endDateProps?: DateTimePickerFieldProps & Record<string, unknown>

    open?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'change', data: Partial<RentalDateModel>): void
    (e: 'confirm'): void

    (e: 'changeOpen', value: boolean): void
}
const emit = defineEmits<Emits>()

const handleChangeStartDate = (value: Date) =>
    emit('change', {
        start_at: value,
        end_at: moment(value).isAfter(moment(props.valueDate?.end_at).utc())
            ? moment(value).utc().toDate()
            : props.valueDate?.end_at
    })
</script>

<template>
    <FullScreenLoader :loading="loading" />
    <ActionButton
        :label="label ?? $t('device.rental.action')"
        @click="$emit('changeOpen', true)"
        v-bind="$attrs" />
    <BaseDialog
        :title="title ?? $t('device.rental.title')"
        :open="!!open"
        @close="$emit('changeOpen', false)"
        @confirm="$emit('confirm')"
        class="dialog-with-dropdown">
        <template #content>
            <StackLayout direction="column" :gap="16" class="w-full">
                <slot />
                <StackLayout direction="row" isResponsive :gap="8" class="w-full">
                    <DateTimePickerField
                        v-if="!!open"
                        :label="$t('device.rental.startAt')"
                        :modelValue="
                            valueDate?.start_at ? formatDate?.(valueDate?.start_at) : undefined
                        "
                        @change="handleChangeStartDate(moment($event).utc().toDate())"
                        class="w-full" />
                    <DateTimePickerField
                        v-if="!!open"
                        :label="$t('device.rental.endAt')"
                        :modelValue="
                            valueDate?.end_at ? formatDate?.(valueDate?.end_at) : undefined
                        "
                        @change="$emit('change', { end_at: moment($event).utc().toDate() })"
                        :minDatetime="moment(valueDate?.start_at).utc().toDate()"
                        class="w-full" />
                </StackLayout>
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.fixed-dialog-dropdown {
    &:deep(.dropdown-content) {
        position: fixed;
        display: flex;
        flex-flow: wrap;
    }
}

.dialog-with-dropdown {
    @media (min-width: 1024px) {
        &:deep(.dropdown-content) {
            position: fixed;
            display: flex;
            flex-flow: wrap;
            & .content-horizontal-timepicker {
                border-right: 1px solid $primary;
            }
        }
        & :deep(footer) {
            & .dropdown-content {
                position: relative;
            }
            & select {
                background-color: var(--color-background-secondary);
                color: var(--color-text);
                & option {
                    color: var(--color-text);
                }
            }
        }
    }
    & :deep(footer) {
        & select {
            background-color: var(--color-background-secondary);
            color: var(--color-text);
            & option {
                color: var(--color-text);
            }
        }
    }
}
</style>
